import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe, Copy, Trash2, CheckCircle2, Clock, RefreshCw, Loader2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import landingPageService, { VercelDomain } from '../../services/landingPageService';

interface HubDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHubChanged?: () => void;
}

const DOMAIN_RE = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// Gerencia o "domínio único": um endereço do cliente (ex.: lp.cliente.com.br)
// que serve todas as LPs publicadas como caminhos /slug — DNS configurado uma vez.
const HubDomainModal: React.FC<HubDomainModalProps> = ({ isOpen, onClose, onHubChanged }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [domains, setDomains] = useState<VercelDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Domínio "ativo" = propriedade verificada E DNS apontando certo
  const isActive = (d: VercelDomain) => d.verified && d.configured === true;

  const loadHub = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await landingPageService.getHub();
      if (response.success && response.data) {
        setDomains(response.data.domains || []);
        setLastError(response.data.lastError || null);
      }
    } catch (error) {
      console.error('Error loading hub:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setNewDomain('');
      loadHub();
    }
  }, [isOpen, loadHub]);

  const handleAddDomain = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!DOMAIN_RE.test(domain)) {
      toast.error(t('landingPages.hub.invalidDomain', 'Domínio inválido. Exemplo: lp.seusite.com.br'));
      return;
    }
    setIsAdding(true);
    try {
      const response = await landingPageService.addHubDomain(domain);
      if (response.success) {
        toast.success(t('landingPages.hub.domainAdded', 'Domínio adicionado! Configure o DNS.'));
        setNewDomain('');
        loadHub();
        onHubChanged?.();
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('landingPages.hub.domainError', 'Erro ao adicionar domínio'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    if (!window.confirm(t('landingPages.hub.confirmRemove', 'Remover o domínio {{domain}}?').replace('{{domain}}', domain))) return;
    try {
      const response = await landingPageService.removeHubDomain(domain);
      if (response.success) {
        toast.success(t('landingPages.hub.domainRemoved', 'Domínio removido'));
        setDomains(prev => prev.filter(d => d.name !== domain));
        onHubChanged?.();
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('landingPages.hub.domainRemoveError', 'Erro ao remover domínio'));
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const response = await landingPageService.syncHub();
      if (response.success) {
        toast.success(t('landingPages.hub.synced', 'Páginas sincronizadas!'));
        setLastError(null);
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('landingPages.hub.syncError', 'Erro ao sincronizar'));
    } finally {
      setIsSyncing(false);
    }
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(t('landingPages.linkCopied', 'Link copiado!')),
      () => toast.error(t('common.error', 'Erro'))
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('landingPages.hub.title', 'Domínio único')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('landingPages.hub.subtitle', 'Todas as páginas publicadas no seu domínio')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Última falha de sincronização (persistida no backend) */}
          {lastError && (
            <div className="flex gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-800 dark:text-red-200">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>
                {t('landingPages.hub.syncFailed', 'A última sincronização falhou — as páginas podem estar desatualizadas no domínio. Use "Sincronizar páginas agora".')}
              </p>
            </div>
          )}

          {/* Explanation */}
          <div className="flex gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>
              {t(
                'landingPages.hub.explanation',
                'Configure um endereço uma única vez (ex.: lp.seusite.com.br) e cada página publicada fica disponível automaticamente em lp.seusite.com.br/nome-da-pagina — sem mexer no DNS de novo.'
              )}
            </p>
          </div>

          {/* Add domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('landingPages.hub.addDomain', 'Adicionar domínio')}
            </label>
            <div className="flex gap-2">
              <Input
                value={newDomain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewDomain(e.target.value)}
                placeholder="lp.seusite.com.br"
                className="flex-1"
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleAddDomain(); }}
              />
              <Button onClick={handleAddDomain} disabled={isAdding || !newDomain.trim()}>
                {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.add', 'Adicionar')}
              </Button>
            </div>
          </div>

          {/* Domain list */}
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : domains.length > 0 ? (
            <div className="space-y-3">
              {domains.map(domain => (
                <div key={domain.name} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {isActive(domain) ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      )}
                      <span className="font-medium text-sm text-gray-900 dark:text-white truncate">{domain.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${isActive(domain) ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                        {isActive(domain)
                          ? t('landingPages.hub.verified', 'Ativo')
                          : t('landingPages.hub.pending', 'Aguardando DNS')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => copyText(`https://${domain.name}`)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        title={t('landingPages.actions.copyLink', 'Copiar link')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveDomain(domain.name)}
                        className="p-1.5 text-gray-400 hover:text-red-500"
                        title={t('common.remove', 'Remover')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* DNS instructions while the domain is not resolving yet */}
                  {!isActive(domain) && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400 space-y-1.5">
                      <p className="font-medium">{t('landingPages.hub.dnsInstructions', 'Crie este registro no DNS do seu domínio:')}</p>
                      <div className="flex items-center gap-2 font-mono bg-gray-50 dark:bg-gray-900 rounded p-2">
                        <span className="truncate">CNAME &nbsp; {domain.name} &nbsp;→&nbsp; cname.vercel-dns.com</span>
                        <button
                          onClick={() => copyText('cname.vercel-dns.com')}
                          className="ml-auto p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
                          title={t('landingPages.actions.copyLink', 'Copiar')}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('landingPages.hub.pathHint', 'Cada página publicada fica disponível em: domínio/nome-da-página (slug).')}
              </p>

              <Button variant="secondary" size="sm" onClick={handleSync} disabled={isSyncing} className="w-full">
                {isSyncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {t('landingPages.hub.syncNow', 'Sincronizar páginas agora')}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
              {t('landingPages.hub.noDomains', 'Nenhum domínio configurado ainda.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HubDomainModal;
